import React, { memo, useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
  SaveButton,
  ImageInput,
  Toolbar,
  NumberInput,
  SelectArrayInput,
  GET_MANY,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import dataProvider from '../../providers/dataProvider';
import { paymentTypeSelectOptions } from '../../constants/paymentType';
import uploadPicture from '../../utils/uploadPicture';
import ImagePickerButton from '../../components/ImagePickerButton/ImagePickerButton';
import removeUnnecessaryProductFields from '../../utils/removeUnnecessaryProductFields';
import RemoveButton from '../../components/RemoveButton/RemoveButton';
import ImgField from '../../components/ImgField/ImgField';
import Loading from '../../components/Loading/Loading';
import DeleteDialog from './components/DeleteDialog';
import PaymentTypeAdditionalField from './components/PaymentTypeAdditionalField';

const parseOptions = (v) =>
  v?.map((i) => {
    if (typeof i === 'number') {
      return i;
    }
    return i.programId;
  });

const ToolBar = (props) => {
  const [isOpenDeleteModal, changeIsOpenDeleteModal] = useState(false);
  const record = useRecordContext();
  const resource = useResourceContext();

  const openDeleteModal = () => {
    changeIsOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    changeIsOpenDeleteModal(false);
  };

  return (
    <>
      <Toolbar
        {...props}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          background: 'none',
        }}
      >
        <SaveButton label="save" />
        <RemoveButton variant="contained" deleteExercise={openDeleteModal} />
      </Toolbar>
      <DeleteDialog
        record={record}
        isOpen={isOpenDeleteModal}
        handleClose={closeDeleteModal}
        resource={resource}
        redirect="list"
      />
    </>
  );
};

const MultipleProductIdsInput = () => {
  // Read/write through the SimpleForm's react-hook-form context so that
  // edits dirty the form and re-enable the SaveButton.
  const { setValue, watch } = useFormContext();
  const value = watch('externalProductId') || '';
  const externalProductIds = value ? value.split(',').filter(Boolean) : [];
  const [currentInput, setCurrentInput] = useState('');

  const updateIds = (newIds) => {
    setValue('externalProductId', newIds.join(','), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && currentInput.trim()) {
      e.preventDefault();
      updateIds([...externalProductIds, currentInput.trim()]);
      setCurrentInput('');
    }
  };

  const handleChange = (e) => {
    setCurrentInput(e.target.value);
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '8px' }}>
        {externalProductIds.map((id, index) => (
          <span
            key={`$sc-${id}`}
            style={{
              background: '#e0e0e0',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '10px',
              margin: '26px 4px 4px 0',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {id}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                updateIds(externalProductIds.filter((_, idx) => idx !== index));
              }}
              style={{
                border: 'none',
                background: 'none',
                marginLeft: '4px',
                cursor: 'pointer',
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={currentInput}
        style={{
          marginTop: '0',
          border: 'none',
          borderBottom: '1px solid #ccc',
          fontSize: '16px',
          padding: '8px 0',
          width: '100%',
          outline: 'none',
        }}
        placeholder="Type a Product ID and press Tab"
      />
    </div>
  );
};

const EditProductForm = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isLoading, changeIsLoading] = useState(false);
  const [options, changeOptions] = useState([]);

  useEffect(() => {
    dataProvider(GET_MANY, 'admin/products/programs').then((res) => changeOptions(res.data));
  }, []);

  if (!record) {
    return <Loading open />;
  }

  const save = (data) => {
    changeIsLoading(true);
    const { picture, ...rawData } = data;
    // Keep the original picture if no new picture was provided
    const originalPicture = record?.picture;
    uploadPicture(picture)
      .then((res) => {
        const newData = removeUnnecessaryProductFields({
          ...rawData,
          picture: res.data.fileName || originalPicture,
          programs: parseOptions(rawData.programs),
        });

        return dataProvider.update('admin/products', { id: record.id, data: newData });
      })
      .then(() => {
        notify('Product saved', { type: 'success' });
        refresh();
      })
      .catch((error) => {
        notify(error?.message || 'Failed to save product', { type: 'error' });
      })
      .finally(() => {
        changeIsLoading(false);
      });
  };

  return (
    <>
      <Loading open={isLoading} />
      <SimpleForm onSubmit={save} toolbar={<ToolBar />}>
        <TextInput source="name" label="Product Name" validate={required()} />
        <TextInput source="link" label="Checkout Link" />
        <TextInput source="cancelUrl" label="Cancel URL" />
        <MultipleProductIdsInput />
        <SelectInput source="paymentType" choices={paymentTypeSelectOptions} />
        <NumberInput source="price" label="Price, $" validate={required()} />
        <PaymentTypeAdditionalField />
        <TextInput
          source="stripePriceId"
          label="Stripe Price Id"
          helperText={
            <span>
              Required field for Stripe Products, not needed for Samcart
              <br />
              if isPaymentPlan is selected should be subscription stripe price id
            </span>
          }
        />
        <NumberInput source="upsellPrice" label="Upsell Price, $" />
        <TextInput source="upsellStripePriceId" label="Upsell Stripe Price Id" />
        <TextInput
          source="mnPlanId"
          label="Mighty Networks Plan ID"
          helperText="If set, purchasing this product will grant access to this MN plan"
        />
        {options.length ? (
          <SelectArrayInput
            source="programs"
            choices={options}
            validate={required()}
            parse={parseOptions}
            format={parseOptions}
          />
        ) : null}
        <TextInput source="description" label="Description" validate={required()} />
        <ImageInput
          source="picture"
          label="Add Product Photo"
          accept={{ 'image/*': [] }}
          validate={required()}
          format={(v) => (typeof v === 'string' ? { picture: v } : v)}
        >
          <ImgField width="100" height="100" source="picture" />
        </ImageInput>
        <ImagePickerButton source="picture" label={false} />
      </SimpleForm>
    </>
  );
};

const EditProduct = (props) => {
  return (
    <Edit {...props}>
      <EditProductForm />
    </Edit>
  );
};

export default memo(EditProduct);
